import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star, Loader2, ShieldCheck, Sparkles, MessageCircleHeart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SectionAccents from "@/components/SectionAccents";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Stars = ({ rating = 5, size = 4 }) => (
    <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className={`w-${size} h-${size} ${i <= rating ? "fill-brand-gold text-brand-gold" : "text-muted-foreground/30"}`} />
        ))}
    </div>
);

const Reviews = () => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState(0);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", city: "", rating: 5, title: "", body: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const params = filter ? { rating: filter } : {};
        const { data } = await api.get("/reviews", { params });
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
    useEffect(() => {
        if (new URLSearchParams(window.location.search).get("write") === "1") setOpen(true);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/reviews", form);
            toast.success("Thanks! Your review is queued for approval 💌");
            setOpen(false);
            setForm({ name: user?.name || "", email: user?.email || "", city: "", rating: 5, title: "", body: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to submit");
        } finally { setSaving(false); }
    };

    const stats = data?.stats;
    const total = stats?.total || 0;

    return (
        <div className="relative max-w-7xl mx-auto px-6 py-14 overflow-hidden" data-testid="reviews-page">
            <SectionAccents variant="reviews" />
            <div className="grid lg:grid-cols-[380px_1fr] gap-10">
                <div className="lg:sticky lg:top-28 self-start">
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Loved by 5,000+ families</div>
                    <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter mt-1">Reviews</h1>
                    <p className="text-muted-foreground mt-2 max-w-md">Real stories from real customers. Every review is verified and moderated.</p>

                    {stats && total > 0 && (
                        <div className="mt-8 rounded-3xl glass p-6">
                            <div className="flex items-baseline gap-3">
                                <div className="font-display text-6xl font-bold text-primary">{stats.average}</div>
                                <div>
                                    <Stars rating={Math.round(stats.average)} size={5} />
                                    <div className="text-xs text-muted-foreground mt-1">{total} verified reviews</div>
                                </div>
                            </div>
                            <div className="mt-5 space-y-2">
                                {[5, 4, 3, 2, 1].map((r) => {
                                    const count = stats.breakdown?.[String(r)] || 0;
                                    const pct = total ? Math.round((count / total) * 100) : 0;
                                    const active = filter === r;
                                    return (
                                        <button
                                            key={r}
                                            data-testid={`filter-rating-${r}`}
                                            onClick={() => setFilter(active ? 0 : r)}
                                            className={`w-full flex items-center gap-3 group ${active ? "text-primary" : ""}`}
                                        >
                                            <div className="flex items-center gap-1 w-10 text-xs shrink-0">{r} <Star className="w-3 h-3 fill-brand-gold text-brand-gold" /></div>
                                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                                <div className={`h-full rounded-full ${active ? "bg-primary" : "bg-brand-gold"}`} style={{ width: `${pct}%` }} />
                                            </div>
                                            <div className="w-10 text-right text-xs text-muted-foreground">{count}</div>
                                        </button>
                                    );
                                })}
                                {filter > 0 && (
                                    <button onClick={() => setFilter(0)} className="text-xs text-primary underline mt-2" data-testid="clear-filter">Clear filter</button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mt-5 rounded-3xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-6">
                        <MessageCircleHeart className="w-6 h-6 mb-2" />
                        <h3 className="font-display text-xl font-bold">Share your Cherishables story</h3>
                        <p className="text-sm opacity-90 mt-1">Your review helps other families create their perfect keepsake.</p>
                        <Button
                            onClick={() => setOpen(true)}
                            data-testid="write-review-btn"
                            className="mt-4 rounded-full bg-white text-primary hover:bg-white/90 h-11 px-5"
                        >Write a review</Button>
                    </div>
                </div>

                <div>
                    {!data ? (
                        <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /></div>
                    ) : data.reviews.length === 0 ? (
                        <div className="rounded-3xl glass p-14 text-center text-muted-foreground text-sm">
                            {filter ? `No ${filter}-star reviews yet.` : "Be the first to leave a review."}
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-4">
                            {data.reviews.map((r, i) => (
                                <motion.article
                                    key={r.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: (i % 8) * 0.04 }}
                                    className="rounded-3xl glass p-6 border border-white/40 relative"
                                    data-testid={`review-card-${r.id}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <Stars rating={r.rating} />
                                        {r.is_featured && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-gold text-primary-foreground">
                                                <Sparkles className="w-3 h-3" /> Featured
                                            </span>
                                        )}
                                    </div>
                                    {r.title && <h3 className="font-display text-lg font-semibold leading-snug">{r.title}</h3>}
                                    <p className="text-sm leading-relaxed mt-1 text-foreground/85">"{r.body}"</p>
                                    <div className="mt-4 flex items-center gap-3 pt-4 border-t border-border/60">
                                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-semibold">
                                            {r.name?.[0]?.toUpperCase() || "C"}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{r.name}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">
                                                {r.city && <span>{r.city}</span>}
                                                {r.city && r.product_name && " · "}
                                                {r.product_name && <span>{r.product_name}</span>}
                                            </div>
                                        </div>
                                        {r.verified_purchase && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                                                <ShieldCheck className="w-3 h-3" /> Verified
                                            </span>
                                        )}
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Submit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg rounded-3xl glass-strong border-0" data-testid="review-form-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Share your Cherishables story</DialogTitle>
                        <DialogDescription>Your review will be published after a quick moderation.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" className="rounded-xl h-11 bg-white/60" />
                            <Input placeholder="City (optional)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl h-11 bg-white/60" />
                        </div>
                        <Input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl h-11 bg-white/60" />
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">Rating</div>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((r) => (
                                    <button type="button" key={r} onClick={() => setForm({ ...form, rating: r })} data-testid={`rating-star-${r}`} className="hover:scale-110 transition-transform">
                                        <Star className={`w-7 h-7 ${r <= form.rating ? "fill-brand-gold text-brand-gold" : "text-muted-foreground/30"}`} />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title" className="rounded-xl h-11 bg-white/60" />
                        <textarea required placeholder="Tell us how it made you feel…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="review-body" className="w-full rounded-2xl border border-input bg-white/60 p-3 text-sm min-h-[120px]" />
                        <Button type="submit" disabled={saving} data-testid="review-submit" className="w-full rounded-full h-12 bg-primary hover:bg-primary/90">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post my review"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Reviews;
